import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import SeverityBadge from "@/components/shared/SeverityBadge";
import ConfirmDialog from "@/components/shared/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import api, { extractApiError, unwrapSingle } from "@/lib/api";
import { useIncidentSocket } from "@/hooks/useIncidentSocket";
import {
  Bell,
  AlertTriangle,
  Cpu,
  Activity,
  RefreshCw,
  Filter,
  Search,
  Clock3,
  Loader2,
  ShieldAlert,
  ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { useLocation, useNavigate } from "react-router-dom";
import LocationLabel from "@/components/shared/LocationLabel";
import { formatDisplayId } from "@/lib/utils";

const CATEGORY_ICON = {
  incident: AlertTriangle,
  device: Cpu,
  behavior: Activity,
};

const CATEGORY_OPTIONS = [
  { value: "all", label: "All Categories" },
  { value: "incident", label: "Incident" },
  { value: "behavior", label: "Behavior" },
  { value: "device", label: "Device" },
];

const SEVERITY_OPTIONS = [
  { value: "all", label: "All Severities" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const STATUS_OPTIONS = ["ACKNOWLEDGED", "RESPONDING", "RESOLVED"];

function normalizeCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function formatTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normalizeAlert(item) {
  return {
    id: item?.id || item?.incident_id || item?.incidentId || "",
    title: item?.title || item?.summary || item?.type || "Untitled alert",
    vehicle: item?.vehicle || item?.plate || item?.vehicle_plate || "—",
    severity: String(item?.severity || "low").toLowerCase(),
    time: item?.time || item?.created_at || item?.timestamp || "",
    category: String(item?.category || "incident").toLowerCase(),
  };
}

function normalizeIncidentDetail(response) {
  return unwrapSingle(response);
}

function toDisplayText(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value === "object") {
    const lat = value?.lat ?? value?.latitude;
    const lon = value?.lon ?? value?.lng ?? value?.longitude;
    if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lon))) {
      return `${Number(lat).toFixed(6)}, ${Number(lon).toFixed(6)}`;
    }
    if (typeof value?.address === "string" && value.address.trim()) return value.address;
    if (typeof value?.name === "string" && value.name.trim()) return value.name;
    try { return JSON.stringify(value); } catch { return "—"; }
  }
  return String(value);
}

export default function AlertsPage() {
  const { can } = useAuth();
  const canUpdateIncidents = can("INCIDENTS.UPDATE");
  const location = useLocation();
  const navigate = useNavigate();

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [meta, setMeta] = useState({ total: 0, page: 1, page_size: 20, pages: 1 });
  const [liveCount, setLiveCount] = useState(0);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [incidentDetail, setIncidentDetail] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState({ status: "", description: "" });
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = { page, page_size: pageSize };
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (severityFilter !== "all") params.severity = severityFilter.toUpperCase();

      const { data } = await api.get("/alerts", { params });
      const list = normalizeCollection(data).map(normalizeAlert).filter((item) => item.id);
      setAlerts(list);
      setMeta(
        data?.meta || {
          total: list.length,
          page,
          page_size: pageSize,
          pages: 1,
        },
      );
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message || "Failed to load alerts.");
      setAlerts([]);
      setMeta({ total: 0, page, page_size: pageSize, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, severityFilter, page, pageSize]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useIncidentSocket(async (incidentData) => {
    const incidentId = incidentData?.id || incidentData?.incident_id || incidentData?.incidentId;

    // Optimistically prepend a placeholder row, then refresh from /alerts
    // to get the properly categorized/formatted backend row.
    if (incidentId) {
      setAlerts((prev) => {
        if (prev.some((item) => item.id === incidentId)) return prev;
        return [
          {
            id: String(incidentId),
            title: "Live incident",
            vehicle: "—",
            severity: "low",
            time: new Date().toISOString(),
            category: "incident",
          },
          ...prev,
        ].slice(0, pageSize);
      });
    }

    setLiveCount((count) => count + 1);
    await fetchAlerts();
  });

  const activeFiltersCount = useMemo(
    () => [categoryFilter !== "all", severityFilter !== "all"].filter(Boolean).length,
    [categoryFilter, severityFilter],
  );

  const openAlertDetail = useCallback(async (alert) => {
    if (!alert?.id) return;

    setSelectedAlert(alert);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError("");
    setIncidentDetail(null);
    setStatusUpdate({ status: "", description: "" });

    try {
      const response = await api.get(`/incidents/${alert.id}`);
      setIncidentDetail(normalizeIncidentDetail(response));
    } catch (err) {
      const { message, details } = extractApiError(err);
      if (details?.fields && Array.isArray(details.fields)) {
        setDetailError(details.fields.map((f) => f.message).join("; ") || message);
      } else {
        setDetailError(message || "Failed to load incident details.");
      }
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const submitStatusUpdate = async () => {
    if (!canUpdateIncidents) {
      toast.error("You do not have permission to update incident status.");
      return;
    }
    if (!selectedAlert?.id) return;

    const payload = {};
    if (statusUpdate.status) payload.status = statusUpdate.status;
    if (statusUpdate.description.trim()) payload.description = statusUpdate.description.trim();

    if (!payload.status && !payload.description) {
      toast.message("Add a status or note first.");
      return;
    }

    setStatusSubmitting(true);
    try {
      await api.patch(`/incidents/${selectedAlert.id}/status`, payload);
      toast.success("Incident status updated.");
      setStatusConfirmOpen(false);
      setDetailOpen(false);
      await fetchAlerts();
    } catch (err) {
      const { message, details, code } = extractApiError(err);
      if (code === "VALIDATION_ERROR" && details?.fields) {
        toast.error(details.fields.map((f) => f.message).join("; ") || message);
      } else {
        toast.error(message || "Failed to update incident status.");
      }
    } finally {
      setStatusSubmitting(false);
    }
  };

  const resetFilters = () => {
    setCategoryFilter("all");
    setSeverityFilter("all");
    setPage(1);
  };

  useEffect(() => {
    const selectedId = location.state?.selectedId;
    if (!selectedId) return;

    const seededAlert =
      alerts.find((item) => item.id === selectedId) ||
      {
        id: selectedId,
        title: "Alert details",
        vehicle: "—",
        severity: "low",
        time: "",
        category: "incident",
      };

    openAlertDetail(seededAlert);
    navigate(location.pathname, { replace: true, state: {} });
  }, [alerts, location.pathname, location.state, navigate, openAlertDetail]);

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <PageHeader
        title="Alerts"
        subtitle="System-wide notifications across all services."
        actions={
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
              Live +{liveCount}
            </span>
            <Button variant="outline" className="gap-2" onClick={fetchAlerts} disabled={loading}>
              <RefreshCw size={14} /> Refresh
            </Button>
          </div>
        }
      />

      <SectionCard>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value="" onChange={() => {}} placeholder="Search is handled by the backend" disabled className="pl-10" />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600">
              <Filter size={13} />
              {activeFiltersCount} active filters
            </div>

            <Select value={categoryFilter} onValueChange={(value) => { setCategoryFilter(value); setPage(1); }}>
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={severityFilter} onValueChange={(value) => { setSeverityFilter(value); setPage(1); }}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                {SEVERITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setPage(1); }}>
              <SelectTrigger className="w-[110px]">
                <SelectValue placeholder="Size" />
              </SelectTrigger>
              <SelectContent>
                {[10, 20, 50].map((size) => (
                  <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="ghost" onClick={resetFilters}>Reset</Button>
          </div>
        </div>
      </SectionCard>

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-medium text-slate-500">{loading ? "Loading alerts..." : `${meta.total} alerts found`}</span>
        <span className="text-xs text-slate-500">Page {meta.page || page} of {meta.pages || 1}</span>
      </div>

      <SectionCard padded={false}>
        {loading && alerts.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 size={18} className="mr-2 animate-spin" /> Loading alerts...
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {alerts.map((alert) => {
              const Icon = CATEGORY_ICON[alert.category] || Bell;
              return (
                <li key={alert.id} className="flex cursor-pointer items-start gap-4 px-5 py-4 transition hover:bg-slate-50/60" onClick={() => openAlertDetail(alert)}>
                  <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                    <Icon size={16} strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{alert.title}</p>
                        <p className="mt-0.5 text-xs text-slate-500"><span className="font-mono">{formatDisplayId(alert.id)}</span> · <span className="font-mono">{alert.vehicle}</span></p>
                      </div>
                      <SeverityBadge severity={alert.severity} />
                    </div>
                    <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-1 font-semibold capitalize text-slate-600">
                        <ClipboardList size={12} /> {alert.category}
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-1 font-semibold text-slate-600">
                        <Clock3 size={12} /> {formatTime(alert.time)}
                      </span>
                    </p>
                  </div>
                </li>
              );
            })}

            {!loading && alerts.length === 0 && (
              <li className="px-5 py-16 text-center text-slate-500">
                <Bell className="mx-auto mb-3 text-slate-200" size={40} />
                <p className="font-medium">No alerts found for the selected filters.</p>
              </li>
            )}
          </ul>
        )}
      </SectionCard>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" disabled={loading || page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>Previous</Button>
        <Button variant="outline" disabled={loading || page >= (meta.pages || 1)} onClick={() => setPage((prev) => Math.min(meta.pages || 1, prev + 1))}>Next</Button>
      </div>

      <Dialog open={detailOpen} onOpenChange={(open) => !open && setDetailOpen(false)}>
        <DialogContent className="sm:max-w-2xl rounded-[28px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">{selectedAlert?.title || "Alert details"}</DialogTitle>
            <DialogDescription className="font-bold text-slate-500">Incident: {formatDisplayId(selectedAlert?.id)}</DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-14 text-slate-500">
              <Loader2 size={22} className="mr-2 animate-spin" /> Loading incident...
            </div>
          ) : detailError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{detailError}</div>
          ) : (
            <div className="space-y-5 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <SeverityBadge severity={selectedAlert?.severity || "low"} />
                <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-bold capitalize text-slate-600">{selectedAlert?.category || "incident"}</span>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Vehicle</p>
                  <p className="mt-1 font-mono text-sm font-semibold text-slate-900">{toDisplayText(selectedAlert?.vehicle)}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Event Time</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">{formatTime(selectedAlert?.time)}</p>
                </div>
              </div>

              {incidentDetail && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Status</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{incidentDetail.status || incidentDetail.current_status || "—"}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Location</p>
                    <LocationLabel locationValue={incidentDetail.location || incidentDetail.address || incidentDetail.place} className="mt-1 text-sm font-semibold text-slate-900" />
                  </div>
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Status Update</Label>
                  <Select
                    value={statusUpdate.status}
                    onValueChange={(value) => setStatusUpdate((prev) => ({ ...prev, status: value }))}
                    disabled={!canUpdateIncidents}
                  >
                    <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((status) => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Reviewer Notes</Label>
                  <Textarea
                    value={statusUpdate.description}
                    onChange={(e) => setStatusUpdate((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Add optional response notes"
                    rows={4}
                    disabled={!canUpdateIncidents}
                  />
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
                {canUpdateIncidents ? (
                  <Button variant="outline" className="gap-2" onClick={() => setStatusConfirmOpen(true)}>
                    <ShieldAlert size={14} /> Update Status
                  </Button>
                ) : null}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={statusConfirmOpen}
        onOpenChange={setStatusConfirmOpen}
        title="Update incident status?"
        description={
          <div className="space-y-2">
            <p>You are about to update <span className="font-bold text-slate-900">{formatDisplayId(selectedAlert?.id)}</span>.</p>
            <p className="text-sm text-slate-500">This will send the status note to the selected incident.</p>
          </div>
        }
        confirmLabel="Apply Update"
        variant="default"
        loading={statusSubmitting}
        onConfirm={submitStatusUpdate}
      />
    </div>
  );
}
